import React, { useEffect, useRef, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Container, BottomNavigation, BottomNavigationAction, Paper, Dialog, DialogActions, DialogContent, DialogTitle, Button, Typography } from '@mui/material';
import ListIcon from '@mui/icons-material/List';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ActiveFiltersBar from './components/ActiveFiltersBar/ActiveFiltersBar';
import SelectionActionBar from './components/SelectionActionBar/SelectionActionBar';
import BreweryActionBar from './components/BreweryActionBar/BreweryActionBar';
import ActionControlsFab from './components/ActionControlsFab/ActionControlsFab';
import { OnboardingTour } from './components/OnboardingTour';

// Import our context providers
import { BeerProvider } from './state/BeerContext';
import { useBeerData } from './state/hooks/useBeerData';
import { useUiState } from './state/hooks/useUiState';
import { useFilters } from './state/hooks/useFilters';
import { useFilteredBeers } from './state/hooks/useBeerSelectors';
import { StorageServiceProvider } from './services/storage/StorageServiceProvider';
import { AnalyticsServiceProviderWrapper, buildTabChangedEvent, useAnalyticsService } from './services/analytics';
import { FeedbackProvider } from './state/hooks/useFeedback';
import FeedbackOverlay from './components/FeedbackOverlay/FeedbackOverlay';
import { BreweryDetailProvider, useBreweryDetail } from './state/hooks/useBreweryDetail';
import BreweryDetailPage from './components/BreweryDetailPage/BreweryDetailPage';
import { OnboardingTourProvider, useOnboardingTour } from './state/hooks/useOnboardingTour';
import OrientationWarning from './components/OrientationWarning/OrientationWarning';
import { AppSettingsProvider, useAppSettings } from './state/providers/AppSettingsProvider';
import { ToastProvider } from './state/providers/ToastProvider';
import { DeviceProvider, useDevice } from './state/providers/DeviceProvider';
import styles from './App.module.css';
import { Beer } from './state/types';
import { useBeer } from './state/BeerContext';
import Brewlette from './components/Brewlette/Brewlette';
import EventListingsDialog from './components/EventListings/EventListingsDialog';
import PintTokensDialog from './components/PintTokens/PintTokensDialog';
import { getBrewletteBeers } from './utils/brewletteLogic';
import {
  clearGlobalSelection,
  selectSingleGlobalCard,
  subscribeToSelection,
  toggleGlobalCardSelection,
} from './components/BeerCard/BeerCardContainer';
import { initialiseClarity, setClarityTag } from './services/clarity';
import { getClientSlugFromHostname } from './utils/getClientSlugFromHostname';
import confetti from 'canvas-confetti';

// Import utilities
import { lazyLoad } from './utils/lazyLoad';

// Import theme
import { theme } from './theme/theme';

// Lazy load components for better performance
const BeerList = lazyLoad(() => import('./components/BeerList/BeerList'));
const Header = lazyLoad(() => import('./components/Header/Header'));
const LoadingScreen = lazyLoad(() => import('./components/LoadingScreen'));

// Theme is now imported from theme.ts

const TAB_LABELS: Record<string, string> = {
  '0': 'All Beers',
  '1': 'My Beers'
};

function fireConfettiBurst(anchor: HTMLElement): void {
  if (typeof window === 'undefined') {
    return;
  }

  const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  if (prefersReducedMotion) {
    return;
  }

  const rect = anchor.getBoundingClientRect();
  if (!rect.width || !rect.height || window.innerWidth === 0 || window.innerHeight === 0) {
    return;
  }

  const origin = {
    x: (rect.left + rect.width / 2) / window.innerWidth,
    y: (rect.top + Math.min(36, rect.height * 0.25)) / window.innerHeight,
  };

  try {
    confetti({
      particleCount: 90,
      spread: 74,
      startVelocity: 34,
      decay: 0.9,
      gravity: 1.05,
      ticks: 220,
      scalar: 1.05,
      origin,
      zIndex: 1265,
    });

    window.setTimeout(() => {
      confetti({
        particleCount: 55,
        spread: 92,
        startVelocity: 28,
        decay: 0.91,
        gravity: 1.15,
        ticks: 200,
        scalar: 0.95,
        origin,
        zIndex: 1265,
      });
    }, 110);
  } catch {
  }
}

function fireConfettiBurstAfterScroll(anchor: HTMLElement): void {
  if (typeof window === 'undefined') {
    return;
  }

  let lastTop = Number.NaN;
  let stableFrames = 0;
  const maxFrames = 60;
  let frames = 0;

  const tick = () => {
    frames++;
    if (!anchor.isConnected) {
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const top = rect.top;

    if (!Number.isNaN(lastTop) && Math.abs(top - lastTop) < 0.75) {
      stableFrames++;
    } else {
      stableFrames = 0;
    }
    lastTop = top;

    if (stableFrames >= 3 || frames >= maxFrames) {
      fireConfettiBurst(anchor);
      return;
    }

    window.requestAnimationFrame(tick);
  };

  window.requestAnimationFrame(tick);
}

function hasAnyAllBeersFilters(filters: ReturnType<typeof useFilters>['filters']): boolean {
  return Boolean(
    filters.searchTerm ||
      filters.typeFilter ||
      filters.breweryFilter ||
      filters.drinkTypeFilter ||
      filters.availabilityFilter ||
      filters.tagFilters.length > 0 ||
      filters.showOnlyAvailable ||
      filters.filterByFavorites
  );
}

function useTabFilterSync(activeTab: string, clearAllFilters: () => void, setShowOnlyFavorites: (v: boolean) => void): void {
  const prevTabRef = useRef(activeTab);

  useEffect(() => {
    if (prevTabRef.current === '0' && activeTab === '1') {
      clearAllFilters();
    }

    if (prevTabRef.current === '1' && activeTab === '0') {
      setShowOnlyFavorites(false);
    }

    prevTabRef.current = activeTab;
  }, [activeTab, clearAllFilters, setShowOnlyFavorites]);
}

function useClarityInitialise(): void {
  useEffect(() => {
    const projectId = (process.env.REACT_APP_CLARITY_PROJECT_ID ?? '').trim();
    if (!projectId) {
      return;
    }

    initialiseClarity(projectId);
  }, []);
}

function useClarityTags(settings: ReturnType<typeof useAppSettings>['settings'], uiTheme: string): void {
  useEffect(() => {
    const projectId = (process.env.REACT_APP_CLARITY_PROJECT_ID ?? '').trim();
    if (!projectId) {
      return;
    }

    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const clientSlug = getClientSlugFromHostname(hostname) ?? '';
    const clientId = settings?.client_id ?? '';

    if (hostname) {
      setClarityTag('hostname', hostname);
    }
    if (clientSlug) {
      setClarityTag('client_slug', clientSlug);
    }
    if (clientId) {
      setClarityTag('client_id', clientId);
    }
    if (uiTheme) {
      setClarityTag('ui_theme', uiTheme);
    }

    type AnalyticsWindow = Window & { gtag?: (...args: unknown[]) => void };
    const gtag = typeof window !== 'undefined' ? (window as AnalyticsWindow).gtag : undefined;
    if (typeof gtag === 'function') {
      gtag('set', 'user_properties', {
        hostname: hostname || undefined,
        client_slug: clientSlug || undefined,
        client_id: clientId || undefined,
        ui_theme: uiTheme || undefined,
      });
    }
  }, [settings?.client_id, uiTheme]);
}

function buildBrewletteBeers(params: {
  activeTab: string;
  hasAllBeersFilters: boolean;
  filteredBeers: Beer[];
  beers: Beer[];
  userInteractions: any;
  showOnlyFavorites: boolean;
  selectedCardIds: Set<string>;
}): Beer[] {
  const {
    activeTab,
    hasAllBeersFilters,
    filteredBeers,
    beers,
    userInteractions,
    showOnlyFavorites,
    selectedCardIds,
  } = params;

  if (activeTab === '0' && hasAllBeersFilters) {
    return filteredBeers;
  }

  return getBrewletteBeers(beers, activeTab, userInteractions, showOnlyFavorites, selectedCardIds);
}

// Brewery detail overlay component - renders the brewery page when a brewery is selected
function BreweryDetailOverlay(): React.ReactElement | null {
  const { selectedBrewery, isOpen, closeBreweryDetail } = useBreweryDetail();
  const { setBreweryFilter } = useFilters();
  
  if (!isOpen || !selectedBrewery) {
    return null;
  }
  
  const handleFilterByBrewery = (breweryName: string) => {
    setBreweryFilter(breweryName);
  };
  
  return (
    <BreweryDetailPage
      brewery={selectedBrewery}
      onClose={closeBreweryDetail}
      onFilterByBrewery={handleFilterByBrewery}
    />
  );
}

// The main app content - separate component to use our focused hooks
export function AppContent(): React.ReactElement {
  // Background image is applied directly in JSX
  // Use our focused hooks instead of the monolithic useBeer
  const { loading } = useBeerData();
  const { ui, setActiveTab } = useUiState();
  const { filters, clearAllFilters, setShowOnlyFavorites } = useFilters();
  const filteredBeers = useFilteredBeers();
  const {
    state: { beers },
    userInteractions
  } = useBeer();
  const analyticsService = useAnalyticsService();
  const { isFeatureEnabled, getBackgroundUrl, getUiTheme, getActionControlsMode, settings } = useAppSettings();
  const { isMobile } = useDevice();
  const [isBrewletteOpen, setIsBrewletteOpen] = useState(false);
  const [brewletteSessionBeers, setBrewletteSessionBeers] = useState<Beer[]>([]);
  const [isBrewletteInfoOpen, setIsBrewletteInfoOpen] = useState(false);
  const [isEventListingsOpen, setIsEventListingsOpen] = useState(false);
  const [isPintTokensOpen, setIsPintTokensOpen] = useState(false);
  const [selectedCardIds, setSelectedCardIds] = useState<Set<string>>(new Set());
  const [isCelebratingSelection, setIsCelebratingSelection] = useState(false);
  const [isUserScrollLocked, setIsUserScrollLocked] = useState(false);
  const [spotlightRect, setSpotlightRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const [spotlightCardId, setSpotlightCardId] = useState<string | null>(null);
  const [hasLoadingScreenFinished, setHasLoadingScreenFinished] = useState(false);
  
  const { completeOnboarding, isOnboardingActive } = useOnboardingTour();

  useEffect(() => {
    if (loading) {
      setHasLoadingScreenFinished(false);
    }
  }, [loading]);
  
  useTabFilterSync(ui.activeTab, clearAllFilters, setShowOnlyFavorites);
  
  const handleOnboardingComplete = () => {
    completeOnboarding(); // Reset demo icons state in context
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    if (newValue === ui.activeTab) {
      return;
    }

    setActiveTab(newValue);

    const tabName = TAB_LABELS[newValue] ?? newValue;
    analyticsService.trackEvent(buildTabChangedEvent(tabName));
  };

  useEffect(() => {
    const unsubscribe = subscribeToSelection((ids) => {
      setSelectedCardIds(ids);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const hasAllBeersFilters = hasAnyAllBeersFilters(filters);

  const brewletteBeers = buildBrewletteBeers({
    activeTab: ui.activeTab,
    hasAllBeersFilters,
    filteredBeers,
    beers,
    userInteractions,
    showOnlyFavorites: filters.showOnlyFavorites,
    selectedCardIds,
  });
  const isBrewletteEnabled = isFeatureEnabled('brewlette');
  const canUseBrewlette = isBrewletteEnabled && brewletteBeers.length >= 2;
  const activeBrewletteBeers = isBrewletteOpen ? brewletteSessionBeers : brewletteBeers;
  const backgroundUrl = getBackgroundUrl();
  const uiTheme = getUiTheme();
  const actionControlsMode = getActionControlsMode();
  const brewletteResultSelectionMode = settings?.brewlette_result_selection_mode ?? 'select_only_result';
  const isEventListingsEnabled = isFeatureEnabled('event_listings');
  const isPintTokensEnabled = isFeatureEnabled('pint_tokens');

  const handleOpenBrewlette = () => {
    if (!isBrewletteEnabled) {
      return;
    }

    if (brewletteBeers.length < 2) {
      setIsBrewletteInfoOpen(true);
      return;
    }

    setBrewletteSessionBeers(brewletteBeers);
    if (brewletteResultSelectionMode === 'select_only_result') {
      clearGlobalSelection();
    }
    setIsBrewletteOpen(true);
  };

  useClarityInitialise();
  useClarityTags(settings, uiTheme);

  useEffect(() => {
    if (!isBrewletteEnabled) {
      if (isBrewletteOpen) {
        setIsBrewletteOpen(false);
      }

      if (brewletteSessionBeers.length > 0) {
        setBrewletteSessionBeers([]);
      }

      return;
    }

    if (isBrewletteOpen && brewletteSessionBeers.length < 2) {
      setIsBrewletteOpen(false);
      setBrewletteSessionBeers([]);
    }
  }, [isBrewletteEnabled, isBrewletteOpen, brewletteSessionBeers]);

  useEffect(() => {
    if (!isUserScrollLocked) {
      return;
    }

    const blockScroll = (event: Event) => {
      event.preventDefault();
    };

    const blockScrollKeys = (event: KeyboardEvent) => {
      const blockedKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', 'Space'];
      if (blockedKeys.includes(event.code) || blockedKeys.includes(event.key)) {
        event.preventDefault();
      }
    };

    window.addEventListener('wheel', blockScroll, { passive: false });
    window.addEventListener('touchmove', blockScroll, { passive: false });
    window.addEventListener('keydown', blockScrollKeys, { passive: false });

    return () => {
      window.removeEventListener('wheel', blockScroll);
      window.removeEventListener('touchmove', blockScroll);
      window.removeEventListener('keydown', blockScrollKeys);
    };
  }, [isUserScrollLocked]);

  useEffect(() => {
    if (!isCelebratingSelection || !spotlightCardId) {
      return;
    }

    const updateSpotlightRect = () => {
      const card = document.getElementById(`beer-card-${spotlightCardId}`);
      if (!card) {
        return;
      }

      const rect = card.getBoundingClientRect();
      setSpotlightRect({
        top: rect.top - 8,
        left: rect.left - 8,
        width: rect.width + 16,
        height: rect.height + 16
      });
    };

    updateSpotlightRect();
    window.addEventListener('scroll', updateSpotlightRect, { passive: true });
    window.addEventListener('resize', updateSpotlightRect);

    return () => {
      window.removeEventListener('scroll', updateSpotlightRect);
      window.removeEventListener('resize', updateSpotlightRect);
    };
  }, [isCelebratingSelection, spotlightCardId]);

  const handleBrewletteResult = (beer: Beer) => {
    const card = document.getElementById(`beer-card-${beer.id}`);
    if (!card) {
      setIsBrewletteOpen(false);
      setIsCelebratingSelection(false);
      setIsUserScrollLocked(false);
      setSpotlightRect(null);
      setSpotlightCardId(null);
      return;
    }

    setIsCelebratingSelection(true);
    setSpotlightCardId(beer.id);
    setSpotlightRect(null);

    window.setTimeout(() => {
      const cardTop = card.getBoundingClientRect().top + window.scrollY;
      const focusY = Math.max(0, Math.floor(window.innerHeight * 0.2) - 16);
      const targetScrollY = Math.max(0, cardTop - focusY);

      window.scrollTo({ top: targetScrollY, behavior: 'smooth' });
      card.classList.add(styles.brewletteHighlight);
      fireConfettiBurstAfterScroll(card);
      window.setTimeout(() => {
        setIsUserScrollLocked(true);
      }, 450);

      window.setTimeout(() => {
        const rect = card.getBoundingClientRect();
        setSpotlightRect({
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16
        });
      }, 420);

      window.setTimeout(() => {
        if (ui.activeTab !== '1') {
          if (brewletteResultSelectionMode === 'keep_existing') {
            if (!selectedCardIds.has(beer.id)) {
              toggleGlobalCardSelection(beer.id);
            }
          } else {
            selectSingleGlobalCard(beer.id);
          }
        }
        card.classList.remove(styles.brewletteHighlight);
        setIsUserScrollLocked(false);
        setIsCelebratingSelection(false);
        setSpotlightRect(null);
        setSpotlightCardId(null);
        setIsBrewletteOpen(false);
        setBrewletteSessionBeers([]);
      }, 3000);
    }, 80);
  };

  return (
    <>
      {/* Loading screen - always render first to prevent flash of loading wheels */}
      <LoadingScreen isLoading={loading} onFinished={() => setHasLoadingScreenFinished(true)} />
      
      {/* Show onboarding tour after loading */}
      {isMobile && isOnboardingActive && hasLoadingScreenFinished && (
        <OnboardingTour onComplete={handleOnboardingComplete} />
      )}
      
      {/* Main container with background image */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          position: 'relative',
          bgcolor: 'var(--secondary-colour)',
          visibility: loading ? 'hidden' : 'visible',
        }}
      >
        {/* Background image with white overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            pointerEvents: 'none',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'var(--secondary-colour)',
              opacity: backgroundUrl ? 0.4 : 0,
            },
            zIndex: 0,
          }}
        />
        {/* Header - logo & search */}
        <Box sx={{ position: 'relative', zIndex: 1100 }}>
          <Header
            showEventListingsMenu={isEventListingsEnabled}
            onOpenEventListings={() => setIsEventListingsOpen(true)}
            showPintTokensMenu={isPintTokensEnabled}
            onOpenPintTokens={() => setIsPintTokensOpen(true)}
          />
        </Box>
        
        {/* Active filters bar - positioned to be visible when header is hidden */}
        {/* Hidden on desktop - mobile only */}
        {isMobile && (
          <Box sx={{ 
            position: 'sticky',
            top: 0,
            zIndex: 1200,
            width: '100%'
          }}>
            <ActiveFiltersBar />
          </Box>
        )}
        {/* Content area */}
        <Box 
          sx={{ 
            flexGrow: 1,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Container 
            maxWidth="md" 
            sx={{ 
              pt: 0,
              pb: 8, // Padding at bottom for fixed navigation
              paddingTop: '0 !important' // Override MUI's default padding
            }}
          >
            <BeerList />
            {isCelebratingSelection && isBrewletteOpen && (
              <Box sx={{ height: '46vh', width: '100%' }} aria-hidden="true" />
            )}
          </Container>
        </Box>
        
        {/* Action controls */}
        {isMobile ? (
          actionControlsMode === 'fab' ? (
            <ActionControlsFab
              showBrewlette={isBrewletteEnabled && !isBrewletteOpen}
              onBrewletteClick={handleOpenBrewlette}
            />
          ) : (
            <>
              {/* Selection Action Bar - appears above bottom nav when a beer is selected */}
              <SelectionActionBar
                showBrewlette={isBrewletteEnabled && !isBrewletteOpen}
                onBrewletteClick={handleOpenBrewlette}
              />

              {/* Brewery Action Bar - overlays selection actions when a brewery is clicked */}
              <BreweryActionBar useThreeButtonSizing={canUseBrewlette && !isBrewletteOpen} />
            </>
          )
        ) : (
          /* Desktop: Only show brewery action bar for viewing/filtering */
          <BreweryActionBar useThreeButtonSizing={false} />
        )}
        {isCelebratingSelection && !spotlightRect && <Box className={styles.brewletteDimOverlay} />}
        {isCelebratingSelection && spotlightRect && (
          <Box
            className={styles.brewletteSpotlight}
            sx={{
              top: `${spotlightRect.top}px`,
              left: `${spotlightRect.left}px`,
              width: `${spotlightRect.width}px`,
              height: `${spotlightRect.height}px`
            }}
          />
        )}
        {/* Brewlette - mobile only */}
        {isMobile && isBrewletteEnabled && (
          <Brewlette
            open={isBrewletteOpen}
            beers={activeBrewletteBeers}
            onClose={() => {
              setIsBrewletteOpen(false);
              setBrewletteSessionBeers([]);
              setIsUserScrollLocked(false);
            }}
            onBeerSelected={handleBrewletteResult}
            suppressStageOverlay={isCelebratingSelection && Boolean(spotlightRect)}
          />
        )}

        <Dialog
          open={isBrewletteInfoOpen}
          onClose={() => setIsBrewletteInfoOpen(false)}
          aria-labelledby="brewlette-info-title"
          PaperProps={{
            sx: {
              background: 'var(--secondary-colour)',
              color: '#f5f5f5',
              borderRadius: '16px',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0 14px 40px rgba(0, 0, 0, 0.5), 0 2px 10px rgba(0, 0, 0, 0.35)',
              backdropFilter: 'blur(10px)',
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle
            id="brewlette-info-title"
            sx={{
              color: 'var(--primary-colour)',
              fontWeight: 700,
              fontSize: '1.25rem',
              lineHeight: 1.2,
              pb: 1,
            }}
          >
            Welcome to Brewlette
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ mt: 0.25, lineHeight: 1.5, color: 'rgba(255, 255, 255, 0.92)' }}>
              To use this feature you need to select 2 or more beers.
            </Typography>
            <Typography sx={{ mt: 1.25, lineHeight: 1.5, color: 'rgba(255, 255, 255, 0.78)' }}>
              Alternatively, filter the beers by brewery, tasting tags, or styles by clicking on their respective links in the beer card.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setIsBrewletteInfoOpen(false)}
              variant="contained"
              color="primary"
              sx={{
                borderRadius: '12px',
                minHeight: 44,
                px: 2,
                fontWeight: 700,
                textTransform: 'none',
                backgroundColor: 'var(--primary-colour)',
                color: 'var(--secondary-colour)',
                border: '1px solid color-mix(in srgb, var(--secondary-colour) 42%, transparent)',
                boxShadow: '0 10px 26px rgba(0, 0, 0, 0.45)',
                '&:hover': {
                  backgroundColor: 'color-mix(in srgb, var(--primary-colour) 84%, black)',
                },
              }}
            >
              Got it
            </Button>
          </DialogActions>
        </Dialog>

        {/* Global feedback overlay */}
        <FeedbackOverlay />
        
        {/* Brewery detail page */}
        <BreweryDetailOverlay />

        <EventListingsDialog
          open={isEventListingsOpen}
          onClose={() => setIsEventListingsOpen(false)}
        />

        <PintTokensDialog
          open={isPintTokensOpen && isPintTokensEnabled}
          onClose={() => setIsPintTokensOpen(false)}
        />
        
        {/* Footer - Bottom Navigation - mobile only */}
        {isMobile && (
          <Paper className={styles.bottomNavPaper} elevation={3}>
            <BottomNavigation
              showLabels
              value={ui.activeTab}
              onChange={handleTabChange}
              className={styles.bottomNav}
              data-tour="navigation-tabs"
            >
              <BottomNavigationAction value="0" label="All Beers" icon={<ListIcon />} />
              <BottomNavigationAction value="1" label="My Beers" icon={<CheckCircleIcon />} />
            </BottomNavigation>
          </Paper>
        )}
      </Box>
    </>
  );
}

// Main App component that wraps everything with the providers
function App(): React.ReactElement {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <OrientationWarning />
      <DeviceProvider>
        <AppSettingsProvider>
          <StorageServiceProvider>
            <AnalyticsServiceProviderWrapper>
              <FeedbackProvider>
                <OnboardingTourProvider>
                  <BeerProvider>
                    <BreweryDetailProvider>
                      <ToastProvider>
                        <AppContent />
                      </ToastProvider>
                    </BreweryDetailProvider>
                  </BeerProvider>
                </OnboardingTourProvider>
              </FeedbackProvider>
            </AnalyticsServiceProviderWrapper>
          </StorageServiceProvider>
        </AppSettingsProvider>
      </DeviceProvider>
    </ThemeProvider>
  );
}

export default App;
